import { useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveVoidSalesInvoice,
  usePendingVoidSalesInvoices,
  useRejectVoidSalesInvoice,
} from "../hooks/useInvoicing";
import { ReasonDialog } from "../components/ReasonDialog";
import type { SalesInvoiceSummary } from "../services/invoicingApi";

const formatDate = (value?: string) => {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMoney = (value?: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 2,
  }).format(value ?? 0);

export const VoidRequestsPage = () => {
  const navigate = useNavigate();
  const notification = useNotification();

  const pendingVoidQuery = usePendingVoidSalesInvoices();
  const approveVoid = useApproveVoidSalesInvoice();
  const rejectVoid = useRejectVoidSalesInvoice();

  const [selectedInvoice, setSelectedInvoice] = useState<SalesInvoiceSummary | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const openApprove = (invoice: SalesInvoiceSummary) => {
    setSelectedInvoice(invoice);
    setApproveOpen(true);
  };

  const openReject = (invoice: SalesInvoiceSummary) => {
    setSelectedInvoice(invoice);
    setRejectOpen(true);
  };

  const handleApprove = (notes: string) => {
    if (!selectedInvoice) return;

    approveVoid.mutate(
      { invoiceId: selectedInvoice.id, payload: { invoiceId: selectedInvoice.id, notes: notes || undefined } },
      {
        onSuccess: () => {
          notification.success(
            `Approved — ${selectedInvoice.invoiceNumber} voided and credit note generated`
          );
          setApproveOpen(false);
          setSelectedInvoice(null);
        },
        onError: () => notification.error("Failed to approve void request"),
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!selectedInvoice) return;

    rejectVoid.mutate(
      { invoiceId: selectedInvoice.id, payload: { invoiceId: selectedInvoice.id, reason } },
      {
        onSuccess: () => {
          notification.success(`Rejected — ${selectedInvoice.invoiceNumber} reverted to previous status`);
          setRejectOpen(false);
          setSelectedInvoice(null);
        },
        onError: () => notification.error("Failed to reject void request"),
      }
    );
  };

  const rows = pendingVoidQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Void Requests</h1>
        <p className="mt-1 text-muted-foreground">
          Review and action pending invoice void requests. Approving a request voids the invoice
          and auto-generates a credit note.
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending Void Requests</CardTitle>
            {rows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                {rows.length}
              </Badge>
            )}
          </div>
          <CardDescription>
            Invoices awaiting void approval — ordered by oldest request first.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {pendingVoidQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading void requests...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">
              No pending void requests at this time.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Order #</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Refund</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Requested At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell
                        className="font-mono text-xs cursor-pointer hover:underline text-blue-600"
                        onClick={() => navigate(`/invoicing/ar/${invoice.id}`)}
                      >
                        {invoice.invoiceNumber}
                      </TableCell>
                      <TableCell>{invoice.customerName}</TableCell>
                      <TableCell>{invoice.orderNumber || "—"}</TableCell>
                      <TableCell>{formatMoney(invoice.totalAmount)}</TableCell>
                      <TableCell>
                        {invoice.refundRequestedWithVoid ? (
                          <div className="flex flex-col gap-0.5">
                            <Badge className="border-blue-400 bg-blue-100 text-blue-700 w-fit text-xs">
                              Refund
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {formatMoney(invoice.requestedRefundAmount ?? 0)} via{" "}
                              {invoice.requestedRefundPaymentMethod}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {(invoice as SalesInvoiceSummary & { voidRequestedByUserName?: string })
                          .voidRequestedByUserName ?? "—"}
                      </TableCell>
                      <TableCell>
                        {formatDate(
                          (invoice as SalesInvoiceSummary & { voidRequestedAt?: string })
                            .voidRequestedAt
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-green-700 border-green-400 hover:bg-green-50"
                            onClick={() => openApprove(invoice)}
                            disabled={approveVoid.isPending || rejectVoid.isPending}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-400 hover:bg-red-50"
                            onClick={() => openReject(invoice)}
                            disabled={approveVoid.isPending || rejectVoid.isPending}
                          >
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve dialog — notes optional */}
      <ReasonDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        title="Approve Void Request"
        description={`Approving will void ${selectedInvoice?.invoiceNumber ?? "this invoice"} and automatically generate a credit note.${selectedInvoice?.refundRequestedWithVoid ? ` A refund of ${formatMoney(selectedInvoice.requestedRefundAmount ?? 0)} via ${selectedInvoice.requestedRefundPaymentMethod} will also be processed.` : ""} This action cannot be undone.`}
        reasonLabel="Approval notes (optional)"
        reasonPlaceholder="Add any notes for the audit trail"
        submitLabel="Approve & void invoice"
        isSubmitting={approveVoid.isPending}
        onSubmit={handleApprove}
      />

      {/* Reject dialog — reason required */}
      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Void Request"
        description={`Rejecting will revert ${selectedInvoice?.invoiceNumber ?? "this invoice"} to its previous status.`}
        reasonLabel="Rejection reason"
        reasonPlaceholder="Explain why this void request is being rejected"
        submitLabel="Reject request"
        isSubmitting={rejectVoid.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
